import React, { useRef, useEffect } from "react";
import { ChatMessage } from "../../../types/chat";
import { chatService } from "../../../services/ChatService";
import { formatChatTimestamp } from "@/utils/helpers";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import env from "@/utils/env";
import { SystemMessageDivider } from "./SystemMessageDivider";

interface MessageListProps {
  messages?: ChatMessage[];
  userId?: string;
  guestEmail?: string;
  isGuest?: boolean;
  chatRoom?: any;
  chatStatus?: 'pending' | 'active' | 'ended';
  onReply?: (message: ChatMessage) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  userId,
  guestEmail,
  isGuest = false,
  onReply,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getFileUrl = (message: ChatMessage) => {
    if (message.content.file?.url) {
      return message.content.file.url;
    }
    return `${env.storageUrl}/storage/${
      message.content.file?.path
    }`;
  };

  const getFileType = (message: ChatMessage) => {
    return message.content.file?.type || "";
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
    {messages?.map((message) => {
            const isSystemMessage = message.content.sender_type === "system";
            const isCurrentUser = isGuest
              ? message.content.metadata.guest_email === guestEmail
              : message.sender_id != null && Number(message.sender_id) === Number(userId);

            if (isSystemMessage) {
              return (
                <SystemMessageDivider key={message.id} body={message.content.body} />
              );
            }

            return (
              <div
                key={message.id}
                className={`group flex items-end gap-1 ${
                  isCurrentUser ? "justify-end" : "justify-start"
                }`}
              >
                <>
                  {isCurrentUser && onReply && (
                    <button
                      onClick={() => onReply(message)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 shrink-0 mb-1"
                      title="Reply"
                    >
                      <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <div
                    className={`max-w-[80%] min-w-0 rounded-lg p-3 shadow-sm ${
                      isCurrentUser
                        ? "bg-blue-500 text-white"
                        : "bg-white border border-gray-200"
                    }`}
                  >
                    <div
                      className={`text-xs ${
                        isCurrentUser ? "text-blue-100" : "text-gray-600"
                      } mb-1 font-semibold break-words`}
                    >
                      {isGuest
                        ? message.content.metadata.guest_name
                        : `${message.sender?.name} (${
                            message.content.sender_role || "User"
                          })`}
                    </div>

                    {message.content.metadata?.reply_to && (
                      <div
                        className={`text-xs mb-1.5 pl-2 border-l-2 truncate ${
                          isCurrentUser
                            ? "border-blue-200 text-blue-100"
                            : "border-gray-300 text-gray-500"
                        }`}
                      >
                        <span className="font-medium">
                          {message.content.metadata.reply_to.sender_name}
                        </span>
                        : {message.content.metadata.reply_to.body}
                      </div>
                    )}

                    {message.content.message_type === "file" ? (
                      <div className="flex flex-col gap-2 min-w-0">
                        {getFileType(message).startsWith("image/") ? (
                          <img
                            src={getFileUrl(message)}
                            alt={message.content.file?.name}
                            className="max-w-[200px] rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                            loading="lazy"
                            onClick={() =>
                              window.open(getFileUrl(message), "_blank")
                            }
                          />
                        ) : (
                          <>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="shrink-0">📎</span>
                              <span className="font-medium break-words">
                                {message.content.file?.name}
                              </span>
                            </div>
                            {getFileType(message).startsWith("video/") && (
                              <video
                                controls
                                className="max-w-[200px] rounded-lg shadow-sm"
                                src={getFileUrl(message)}
                              />
                            )}
                            {getFileType(message).startsWith("audio/") && (
                              <audio
                                controls
                                className="max-w-[200px]"
                                src={getFileUrl(message)}
                              />
                            )}
                            <button
                              onClick={async () => {
                                const blob = await chatService.downloadAttachment(
                                  message.id,
                                  message.content.file?.name || ""
                                );
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = message.content.file?.name || "";
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                if (a.parentNode) {
                                  a.parentNode.removeChild(a);
                                }
                              }}
                              className="text-xs underline hover:opacity-80 flex items-center gap-1 mt-1"
                            >
                              <span>⬇️</span> Download
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm break-words whitespace-pre-wrap">
                        {message.content.body}
                      </div>
                    )}

                    <div
                      className={`text-xs mt-1 ${
                        isCurrentUser ? "text-blue-100" : "text-gray-500"
                      }`}
                    >
                      {formatChatTimestamp(message.created_at)}
                    </div>
                  </div>
                  {!isCurrentUser && onReply && (
                    <button
                      onClick={() => onReply(message)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 shrink-0 mb-1"
                      title="Reply"
                    >
                      <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              </div>
            );
          })}
      <div ref={messagesEndRef} />
    </div>
  );
};
